import React, { useState, useEffect, useMemo } from 'react';
import { FiUsers, FiUserPlus, FiEdit2, FiTrash2, FiSearch } from 'react-icons/fi';
import Card from '../../components/Card/Card';
import Badge from '../../components/Badge/Badge';
import Button from '../../components/Button/Button';
import DataTable from '../../components/DataTable/DataTable';
import Modal from '../../components/Modal/Modal';
import Input from '../../components/Input/Input';
import Select from '../../components/Select/Select';
import api from '../../api';
import './Members.css';

const INITIAL_FORM = {
  Name: '', MemberType: 'Student', Class: '', RollNo: '', Department: '', Phone: '', Email: '', Status: 'Active'
};

const Members = ({ showAlert, showConfirm }) => {
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [sortConfig, setSortConfig] = useState({ key: 'Name', direction: 'asc' });

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/members.php');
      setMembers(res.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { fetchMembers(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = editingId ? { ...form, MemberId: editingId } : form;
    try {
      const method = editingId ? api.put : api.post;
      const res = await method('/members.php', body);
      const result = res.data;
      
      showAlert("Success", editingId ? "Member updated!" : "Member registered!", "success");
      setShowModal(false);
      setEditingId(null);
      setForm(INITIAL_FORM);
      fetchMembers();
    } catch (err) {
      showAlert("Action failed", err.message, "confirm");
    }
  };

  const handleDelete = async (id) => {
    showConfirm("Delete Member", "Are you sure you want to delete this member? This cannot be undone.", async () => {
      try {
        const res = await api.delete(`/members.php?id=${id}`);
        const result = res.data;
        fetchMembers();
        showAlert("Deleted", "Member removed successfully.", "success");
      } catch (err) { 
        showAlert("Delete failed", err.message, "confirm");
      }
    });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filtered = useMemo(() => {
    let data = members.filter(m => 
      m.Name?.toLowerCase().includes(search.toLowerCase()) || 
      m.Email?.toLowerCase().includes(search.toLowerCase()) || 
      m.Phone?.includes(search)
    );

    if (sortConfig.key) {
      data.sort((a, b) => {
        const aVal = a[sortConfig.key] || '';
        const bVal = b[sortConfig.key] || '';
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return data;
  }, [members, search, sortConfig]);

  const columns = [
    { key: 'Name', label: 'Member', sortable: true, render: (val) => (
      <div className="member-cell"><div className="member-cell__avatar">{val?.charAt(0)}</div><span className="member-cell__name">{val}</span></div>
    )},
    { key: 'Email', label: 'Contact', render: (val, row) => (
      <div style={{fontSize:'13px'}}><p style={{margin:0}}>{val}</p><p style={{margin:0, color:'var(--text-muted)'}}>{row.Phone}</p></div>
    )},
    { key: 'MemberType', label: 'Type', sortable: true, render: (val) => <Badge variant="neutral">{val}</Badge> },
    { key: 'RollNo', label: 'Univ. Roll No', sortable: true, render: (val) => <Badge variant="neutral">{val}</Badge> },
    { key: 'Class', label: 'Class/Dept', sortable: true, render: (_, row) => row.Class || row.Department || '—' },
    { key: 'Status', label: 'Status', sortable: true, render: (val) => <Badge variant={val === 'Active' ? 'success' : 'neutral'} dot>{val}</Badge> },
    { key: 'actions', label: 'Actions', render: (_, row) => (
      <div className="table-actions">
        <button className="table-action-btn table-action-btn--edit" onClick={() => { setEditingId(row.MemberId); setForm(row); setShowModal(true); }}><FiEdit2 /></button>
        <button className="table-action-btn table-action-btn--delete" onClick={() => handleDelete(row.MemberId)}><FiTrash2 /></button>
      </div>
    )}
  ];

  return (
    <div className="members-page">
      <div className="page-header">
        <div><h2 className="page-header__title">Members</h2><p className="page-header__sub">Register and manage library membership</p></div>
        <Button variant="primary" icon={<FiUserPlus />} onClick={() => { setEditingId(null); setForm(INITIAL_FORM); setShowModal(true); }}>Add Member</Button>
      </div>

      <div className="members-kpi">
         <Card padding="md" className="kpi-card kpi-card--primary">
           <div className="kpi-card__inner"><div><p className="kpi-card__label">Total Members</p><p className="kpi-card__value">{members.length}</p></div><div className="kpi-card__icon"><FiUsers /></div></div>
         </Card>
      </div>

      <Card padding="none">
        <div className="shared-toolbar">
          <div className="shared-search-box">
            <FiSearch className="shared-search-box__icon" />
            <input 
              type="text" 
              placeholder="Search members by name, email or phone…" 
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
              className="shared-search-box__input" 
            />
          </div>
        </div>
        <DataTable columns={columns} data={filtered} loading={loading} onSort={handleSort} sortConfig={sortConfig} />
      </Card>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingId ? 'Edit Member' : 'Register New Member'}>
        <form onSubmit={handleSubmit}>
          <div className="form-grid form-grid--2">
            <Input id="Name" name="Name" label="Full Name" value={form.Name} onChange={handleChange} required />
            <Select id="MemberType" name="MemberType" label="Member Type" options={[{value:'Student',label:'Student'},{value:'Teacher',label:'Teacher'},{value:'Staff',label:'Staff'}]} value={form.MemberType} onChange={handleChange} />
            {form.MemberType === 'Student' ? (
              <><Input id="Class" name="Class" label="Class" value={form.Class} onChange={handleChange} /><Input id="RollNo" name="RollNo" label="University Roll No" value={form.RollNo} onChange={handleChange} /></>
            ) : (
              <Input id="Department" name="Department" label="Department" value={form.Department} onChange={handleChange} className="form-grid__span-2" />
            )}
            <Input id="Phone" name="Phone" label="Phone No" value={form.Phone} onChange={handleChange} required />
            <Input id="Email" name="Email" label="Email Address" type="email" value={form.Email} onChange={handleChange} required />
            <Select id="Status" name="Status" label="Account Status" options={[{value:'Active',label:'Active'},{value:'Inactive',label:'Inactive'}]} value={form.Status} onChange={handleChange} />
          </div>
          <div style={{marginTop:'24px', display:'flex', gap:'12px'}}><Button type="submit" variant="primary">Save Member</Button><Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button></div>
        </form>
      </Modal>
    </div>
  );
};

export default Members;
